import {
  AuthenticateResult,
  BotCategorizationData,
  BotConfiguration,
  BotCreationAugmentationFlags,
  BotEnvironmentData,
  BotFlow,
  BotId,
  BotMetadata,
  BotVersion,
  UserProfile
} from '@feedyou/schema'
import { VersionIncrement } from '@feedyou/utils'
import { serverApi, ServerApiTag } from '../../../store/serverApi'
import { bindSideEffects } from '../../../lib/rtkq/bindSideEffects'
import { sortObjectKeys } from '../../../lib/object/sortObjectKeys'
import {
  notifyBotAdoptionFailure,
  restoreSuccessToast,
  toastArchiveSuccess,
  toastDeleteSuccess,
  toastNotifySaveChangesSuccess,
  toastNotifyUpdateConfigFailure,
  toastNotifyUpdateConfigSuccess
} from './toasting'
import { getAllReservedBotIds } from '../utils/getAllReservedBotIds'
import { rehydrateQueryResultFromCache } from '../../../store/serverApi/rehydrateQueryResultFromCache'
import { WizardConfiguration } from '../types'

const Tag = ServerApiTag

export const botsApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    getBotsMetadata: builder.query<
      {
        metadata: BotMetadata[]
        existingBotIds: string[]
        deletedBotIds: string[]
        allReservedIds: string[]
      },
      { fromBotId?: string }
    >({
      query: ({ fromBotId }) => ({
        url: 'bots-metadata',
        headers: { 'x-user-authentication-against-bot-id': fromBotId }
      }),

      transformResponse: (response: { metadata: BotMetadata[]; deletedBotIds: string[] }) => {
        const existingBotIds = response.metadata.map(metadata => metadata.id)

        return {
          metadata: response.metadata,
          existingBotIds,
          deletedBotIds: response.deletedBotIds,
          allReservedIds: getAllReservedBotIds(existingBotIds, response.deletedBotIds)
        }
      },
      onQueryStarted: rehydrateQueryResultFromCache,
      providesTags: [Tag.BOTS_METADATA_LIST]
    }),

    getBotConfiguration: builder.query<
      { configuration: BotConfiguration; directLineSecret?: string },
      { id: BotId }
    >({
      query: ({ id }) => `bots/${id}/configuration`,
      onQueryStarted: rehydrateQueryResultFromCache,
      providesTags: (_, __, arg) => [Tag.BOT_CONFIG(arg.id)]
    }),

    getBotFlow: builder.query<BotFlow, { id: BotId; version: string | undefined }>({
      query: ({ id, version }) => `bots/${id}/versions/${version}/flow`,
      transformResponse: (response: { flow: BotFlow }) => sortObjectKeys(response.flow),
      onQueryStarted: rehydrateQueryResultFromCache,

      providesTags: (_, __, arg) => [Tag.BOT_FLOW(arg.id, arg.version)]
    }),

    getLatestSemver: builder.query<string, { id: BotId }>({
      query: ({ id }) => `bots/${id}/versions/latest-semver`,
      providesTags: (_, __, arg) => [Tag.BOT_LATEST_SEMVER(arg.id)]
    }),

    getVersionListPageForBot: builder.query<
      { versions: BotVersion[]; totalPageCount: number },
      { botId: BotId; limit: number; pageNr: number }
    >({
      query: ({ botId, limit, pageNr }) => ({
        url: `bots/${botId}/versions/`,
        params: { limit, pageNr }
      }),
      providesTags: (_, __, arg) => [Tag.BOT_VERSIONS_LIST(arg.botId)]
    }),

    // This is not really a bot-related endpoint, but I like keeping it here because of tag invalidation (and I don't really have a better place for it)
    getCurrentUserPermissionsForBot: builder.query<AuthenticateResult, { botId: BotId }>({
      query: ({ botId }) => ({ url: `authenticate`, params: { botId } }),
      onQueryStarted: rehydrateQueryResultFromCache,
      providesTags: (_, __, arg) => [Tag.USER_PERMISSIONS_FOR_BOT(arg.botId)]
    }),

    getVersionDiff: builder.query<string | null, { botId: BotId; version: string }>({
      query: ({ botId, version }) => `bots/${botId}/versions/${version}/diff`,
      transformResponse: (res: { diff: string | null }) => res.diff,
      providesTags: (_, __, arg) => [Tag.BOT_VERSION_DIFF(arg.botId, arg.version)]
    }),

    getLegacySchema: builder.query<any, { botId: BotId }>({
      query: ({ botId }) => `bots/${botId}/legacyBotSchema`,
      providesTags: []
    }),

    updateBotConfiguration: builder.mutation<
      void,
      {
        id: BotId
        configurationPatch: Pick<
          BotConfiguration,
          'previewUrl' | 'powerbiEmbedUrl' | 'designerTheme' | 'permissions'
        >
      }
    >({
      query: ({ id, configurationPatch }) => ({
        method: 'PATCH',
        url: `bots/${id}/configuration`,
        body: { configurationPatch }
      }),
      onQueryStarted: bindSideEffects({
        success: [toastNotifyUpdateConfigSuccess],
        failure: [toastNotifyUpdateConfigFailure]
      }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_CONFIG(arg.id),
        Tag.USER_PERMISSIONS_FOR_BOT(arg.id),
        Tag.BOTS_METADATA_LIST
      ]
    }),

    createNewBot: builder.mutation<
      void,
      {
        id: BotId
        categorization: BotCategorizationData
        author: UserProfile | undefined
        augmentationFlags: BotCreationAugmentationFlags
      }
    >({
      query: ({ id, categorization, author, augmentationFlags }) => ({
        url: `bots`,
        body: { botId: id, categorization, author, augmentationFlags },
        method: 'POST'
      }),
      invalidatesTags: [Tag.BOTS_METADATA_LIST]
    }),

    createNewVersion: builder.mutation<
      { createdVersion: string },
      {
        id: BotId
        increment: VersionIncrement
        flow: BotFlow
        author: UserProfile | undefined
        versionTitle: string
      }
    >({
      query: ({ id, flow, increment, author, versionTitle }) => ({
        url: `bots/${id}/versions`,
        body: { increment, flow, author, versionTitle },
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({
        success: [(params, result) => toastNotifySaveChangesSuccess(result.data?.createdVersion)]
      }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_VERSIONS_LIST(arg.id),
        Tag.BOT_LATEST_SEMVER(arg.id)
      ]
    }),

    duplicateToNewBot: builder.mutation<
      void,
      {
        sourceBotId: BotId
        sourceVersion: string
        newBotId: BotId
        categorization: BotCategorizationData
        author: UserProfile | undefined
        versionTitle: string
        newBotInitialVersion?: string
        environmentData?: BotEnvironmentData
      }
    >({
      query: ({ sourceBotId, sourceVersion, ...body }) => ({
        url: `bots/${sourceBotId}/versions/${sourceVersion}/duplicate-to-new-bot`,
        body,
        method: 'POST'
      }),
      invalidatesTags: [Tag.BOTS_METADATA_LIST]
    }),

    createTestEnvironment: builder.mutation<
      void,
      { environmentGroupId: string; sourceProdId: BotId; newBotId: BotId; author?: UserProfile }
    >({
      query: ({ environmentGroupId, sourceProdId, newBotId, author }) => ({
        url: logAndPass(
          `bot-environment-groups/${encodeURIComponent(environmentGroupId)}/create-test-environment`
        ),
        body: { sourceProdId, newBotId, author },
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({
        failure: [(params, result) => notifyBotAdoptionFailure(params.newBotId, result.error.data)]
      }),
      invalidatesTags: (_, __, arg) => [Tag.BOTS_METADATA_LIST]
    }),

    moveTestToProd: builder.mutation<
      void,
      { environmentGroupId: string; sourceTestId: BotId; targetProdId: BotId; author?: UserProfile }
    >({
      query: ({ environmentGroupId, sourceTestId, targetProdId, author }) => ({
        url: `bot-environment-groups/${encodeURIComponent(environmentGroupId)}/move-test-to-prod`,
        body: { sourceTestId, targetProdId, author },
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({
        failure: [
          (params, result) => notifyBotAdoptionFailure(params.targetProdId, result.error.data)
        ]
      }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_VERSIONS_LIST(arg.targetProdId),
        Tag.BOT_LATEST_SEMVER(arg.targetProdId),
        Tag.BOTS_METADATA_LIST // enabled/disabled
      ]
    }),

    moveProdToTest: builder.mutation<
      void,
      { environmentGroupId: string; sourceProdId: BotId; targetTestId: BotId; author?: UserProfile }
    >({
      query: ({ environmentGroupId, sourceProdId, targetTestId, author }) => ({
        url: `bot-environment-groups/${encodeURIComponent(environmentGroupId)}/move-prod-to-test`,
        body: { sourceProdId, targetTestId, author },
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({
        failure: [
          (params, result) => notifyBotAdoptionFailure(params.targetTestId, result.error.data)
        ]
      }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_VERSIONS_LIST(arg.targetTestId),
        Tag.BOT_LATEST_SEMVER(arg.targetTestId),
        Tag.BOTS_METADATA_LIST // enabled/disabled
      ]
    }),

    revertToVersion: builder.mutation<
      void,
      { botId: BotId; targetVersion: string; author?: UserProfile }
    >({
      query: ({ botId, targetVersion, author }) => ({
        url: `bots/${botId}/revert`,
        body: { targetVersion, author },
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({
        failure: [(params, result) => notifyBotAdoptionFailure(params.botId, result.error.data)]
      }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_VERSIONS_LIST(arg.botId),
        Tag.BOT_LATEST_SEMVER(arg.botId)
      ]
    }),

    adoptExistingVersion: builder.mutation<
      void,
      {
        targetBotId: BotId
        sourceBotId: BotId
        sourceVersion?: string
        author: UserProfile | undefined
        versionTitle?: string
        versionIncrement?: VersionIncrement | 'adopt_source'
      }
    >({
      query: ({
        sourceBotId,
        targetBotId,
        sourceVersion,
        author,
        versionTitle,
        versionIncrement
      }) => ({
        url: `bots/${targetBotId}/adopt-existing-version`,
        body: { sourceBotId, sourceVersion, author, versionTitle, versionIncrement },
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({
        failure: [
          (params, result) => {
            notifyBotAdoptionFailure(params.targetBotId, result.error.data)
          }
        ]
      }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_VERSIONS_LIST(arg.targetBotId),
        Tag.BOT_LATEST_SEMVER(arg.targetBotId)
      ]
    }),

    enable: builder.mutation<void, { id: BotId }>({
      query: ({ id }) => ({
        url: `bots/${id}/enable`,
        method: 'POST'
      }),
      invalidatesTags: (_, __, arg) => [Tag.BOT_CONFIG(arg.id), Tag.BOTS_METADATA_LIST]
    }),

    disable: builder.mutation<void, { id: BotId }>({
      query: ({ id }) => ({
        url: `bots/${id}/disable`,
        method: 'POST'
      }),
      invalidatesTags: (_, __, arg) => [Tag.BOT_CONFIG(arg.id), Tag.BOTS_METADATA_LIST]
    }),

    archive: builder.mutation<void, { botId: BotId; author: UserProfile | undefined }>({
      query: ({ botId, author }) => ({
        url: `bots/${botId}/archive`,
        method: 'POST',
        body: { author }
      }),
      onQueryStarted: bindSideEffects({ success: [params => toastArchiveSuccess(params.botId)] }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_CONFIG(arg.botId),
        Tag.BOT_LATEST_SEMVER(arg.botId),
        Tag.BOT_VERSIONS_LIST(arg.botId),
        Tag.BOTS_METADATA_LIST
      ]
    }),

    delete: builder.mutation<
      void,
      { botId: BotId; author: UserProfile | undefined; force?: boolean }
    >({
      query: ({ botId, author, force = false }) => ({
        url: `bots/${botId}/delete`,
        method: 'POST',
        body: { author, force: force },
        responseHandler: 'text'
      }),
      onQueryStarted: bindSideEffects({ success: [params => toastDeleteSuccess(params.force)] }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_CONFIG(arg.botId),
        Tag.BOT_LATEST_SEMVER(arg.botId),
        Tag.BOT_VERSIONS_LIST(arg.botId),
        Tag.BOTS_METADATA_LIST
      ]
    }),

    restore: builder.mutation<void, { botId: BotId; author: UserProfile | undefined }>({
      query: ({ botId }) => ({
        url: `bots/${botId}/restore`,
        method: 'POST'
      }),
      onQueryStarted: bindSideEffects({ success: [params => restoreSuccessToast(params.botId)] }),
      invalidatesTags: (_, __, arg) => [
        Tag.BOT_CONFIG(arg.botId),
        Tag.BOT_LATEST_SEMVER(arg.botId),
        Tag.BOT_VERSIONS_LIST(arg.botId),
        Tag.BOTS_METADATA_LIST
      ]
    }),

    getWizardConfiguration: builder.query<{ wizardData: WizardConfiguration }, { id: BotId }>({
      query: ({ id }) => `bots/${id}/wizard`,
      onQueryStarted: rehydrateQueryResultFromCache,
      providesTags: (_, __, arg) => [Tag.BOT_WIZARD(arg.id)]
    }),

    updateWizardConfiguration: builder.mutation<
      void,
      { id: BotId; wizardData: WizardConfiguration }
    >({
      query: ({ id, wizardData }) => ({
        method: 'PUT',
        url: `bots/${id}/wizard`,
        body: { wizardData }
      }),
      invalidatesTags: (_, __, arg) => [Tag.BOT_WIZARD(arg.id)]
    })
  })
})

const logAndPass = <T extends any>(param: T): T => {
  console.log(param)
  return param
}
